import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { WorkflowSchedule } from "../types";

interface ScheduleModalProps {
    show: boolean;
    schedule: WorkflowSchedule | null | undefined;
    isInstant: boolean;
    minIntervalMinutes: number;
    readOnly: boolean;
    onSave: (schedule: WorkflowSchedule) => void;
    onClose: () => void;
}

const browserTimezone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/** When the scenario runs (ScheduleCalculator in the cabinet). */
const ScheduleModal = ({ show, schedule, isInstant, minIntervalMinutes, readOnly, onSave, onClose }: ScheduleModalProps) => {
    const { t } = useTranslation(['workflow']);
    const [draft, setDraft] = useState<WorkflowSchedule>({ type: 'ondemand' });

    useEffect(() => {
        if (show) {
            setDraft(schedule || (isInstant ? { type: 'immediately' } : { type: 'interval', interval: Math.max(15, minIntervalMinutes), timezone: browserTimezone() }));
        }
    }, [show, schedule, isInstant, minIntervalMinutes]);

    const types: WorkflowSchedule['type'][] = isInstant ? ['immediately', 'ondemand'] : ['interval', 'daily', 'weekly', 'monthly', 'once', 'cron', 'ondemand'];
    const set = (patch: Partial<WorkflowSchedule>) => setDraft({ ...draft, ...patch });
    const toggleDay = (day: number) => {
        const days = draft.days || [];
        set({ days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b) });
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton><Modal.Title>{t('workflow:schedule')}</Modal.Title></Modal.Header>
            <Modal.Body>
                <fieldset disabled={readOnly}>
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:runScenario')}</label>
                        <select className="form-select" value={draft.type} onChange={(e) => setDraft({ type: e.target.value as WorkflowSchedule['type'], timezone: draft.timezone || browserTimezone() })}>
                            {types.map((type) => <option key={type} value={type}>{t('workflow:schedule_' + type)}</option>)}
                        </select>
                    </div>

                    {draft.type === 'interval' &&
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:intervalMinutes')}</label>
                            <input type="number" min={minIntervalMinutes} className="form-control" value={draft.interval ?? ''} onChange={(e) => set({ interval: parseInt(e.target.value, 10) || undefined })} />
                            <div className="form-text">{t('workflow:minIntervalHint', { minutes: minIntervalMinutes })}</div>
                        </div>
                    }
                    {draft.type === 'once' &&
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:dateTime')}</label>
                            <input type="datetime-local" className="form-control" value={(draft.date || '').slice(0, 16)} onChange={(e) => set({ date: e.target.value })} />
                        </div>
                    }
                    {(draft.type === 'daily' || draft.type === 'weekly' || draft.type === 'monthly') &&
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:time')}</label>
                            <input type="time" className="form-control" value={draft.time || ''} onChange={(e) => set({ time: e.target.value })} />
                        </div>
                    }
                    {draft.type === 'weekly' &&
                        <div className="mb-3 d-flex flex-wrap gap-1">
                            {WEEKDAYS.map((d) => (
                                <button key={d} type="button" className={"btn btn-sm " + ((draft.days || []).includes(d) ? 'btn-primary' : 'btn-light')} onClick={() => toggleDay(d)}>
                                    {t('workflow:weekday_' + d)}
                                </button>
                            ))}
                        </div>
                    }
                    {draft.type === 'monthly' &&
                        <div className="mb-3 d-flex flex-wrap gap-1">
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                <button key={d} type="button" className={"btn btn-sm " + ((draft.days || []).includes(d) ? 'btn-primary' : 'btn-light')} style={{ width: 38 }} onClick={() => toggleDay(d)}>{d}</button>
                            ))}
                        </div>
                    }
                    {draft.type === 'cron' &&
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:cronExpression')}</label>
                            <input className="form-control font-monospace" placeholder="0 9 * * 1-5" value={draft.expression || ''} onChange={(e) => set({ expression: e.target.value })} />
                        </div>
                    }
                    {!['immediately', 'ondemand', 'interval'].includes(draft.type) &&
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:timezone')}</label>
                            <input className="form-control" value={draft.timezone || ''} onChange={(e) => set({ timezone: e.target.value })} placeholder="Europe/Moscow" />
                        </div>
                    }
                </fieldset>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onClose}>{t('workflow:cancel')}</button>
                {!readOnly && <button type="button" className="btn btn-primary" onClick={() => onSave(draft)}>{t('workflow:apply')}</button>}
            </Modal.Footer>
        </Modal>
    );
};

export default ScheduleModal;
